import { theme as antdAlgorithms, type ThemeConfig } from 'antd';
import { palettes, radius, typography, type ThemeMode } from './tokens';

/**
 * Maps the OneLattice token palette onto an AntD v5 ThemeConfig. Dark uses the darkAlgorithm as a
 * base then overrides seed + surface tokens so AntD's derived palette matches our calm surfaces.
 */
export function buildAntdTheme(mode: ThemeMode): ThemeConfig {
  const p = palettes[mode];
  const algorithm =
    mode === 'dark'
      ? [antdAlgorithms.darkAlgorithm, antdAlgorithms.compactAlgorithm]
      : [antdAlgorithms.defaultAlgorithm, antdAlgorithms.compactAlgorithm];

  return {
    algorithm,
    token: {
      colorPrimary: p.primary,
      colorInfo: p.info,
      colorSuccess: p.positive,
      colorWarning: p.attention,
      colorError: p.negative,

      colorBgBase: p.bgBase,
      colorBgLayout: p.bgBase,
      colorBgContainer: p.bgContainer,
      colorBgElevated: p.bgElevated,
      colorBorder: p.border,
      colorBorderSecondary: p.borderSubtle,

      colorText: p.textPrimary,
      colorTextSecondary: p.textSecondary,
      colorTextTertiary: p.textTertiary,
      colorTextQuaternary: p.textTertiary,

      fontFamily: typography.fontFamily,
      fontFamilyCode: typography.fontFamilyMono,
      fontSize: typography.size,
      fontSizeSM: typography.sizeSm,
      fontSizeLG: typography.sizeLg,
      fontSizeHeading3: typography.sizeH3,
      fontSizeHeading4: typography.sizeH4,
      fontSizeHeading5: typography.sizeH5,
      lineHeight: typography.lineHeight,

      borderRadius: radius.md,
      borderRadiusLG: radius.lg,
      borderRadiusSM: radius.sm,

      controlHeight: 32,
      wireframe: false,
      // Enhanced focus visibility (WCAG 2.2 SC 2.4.13).
      colorPrimaryBorder: p.focusRing,
    },
    components: {
      Layout: {
        headerBg: p.bgContainer,
        headerHeight: 52,
        headerPadding: '0 16px',
        siderBg: p.bgContainer,
        bodyBg: p.bgBase,
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: mode === 'dark' ? '#22303a' : '#e6f0f7',
        itemHoverBg: mode === 'dark' ? '#1c2830' : '#eef3f7',
        itemSelectedColor: p.primary,
        iconSize: 16,
      },
      Card: { colorBorderSecondary: p.border },
      Table: {
        headerBg: p.bgSubtle,
        rowHoverBg: p.bgSubtle,
        borderColor: p.borderSubtle,
      },
      Tabs: { horizontalItemGutter: 20 },
      Tooltip: {
        colorBgSpotlight: p.bgElevated,
        colorTextLightSolid: p.textPrimary,
      },
      Tag: { defaultBg: p.bgSubtle, defaultColor: p.textSecondary },
      Segmented: { itemSelectedBg: p.bgElevated },
    },
  };
}
